import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  fetchPeriodo, toggleAzione, toggleMit, toggleTargetPeriodo, setRecurrenceOverride,
} from '../../lib/api';
import { useOptimisticToggle } from '../../lib/useOptimisticToggle';

/**
 * usePeriodoData(gran, label, krFilter) — carica i dati di un periodo
 * (`anno`/`quarter`/`mese`/`settimana`/`giorno`) e gestisce stato locale
 * + toggle optimistic per azioni/MIT/target periodo.
 *
 * Side-effect: ascolta `qp:quick-add-created` per ricaricare quando l'utente
 * aggiunge un'azione/MIT da Quick Add.
 *
 * Toggle ricorrenze:
 *  - Se l'item è un'occorrenza virtuale (id contiene '::' e is_occurrence=true),
 *    il toggle scrive un override su `recurrence_overrides` invece di toccare
 *    il parent. La UI applica comunque optimistic update sulla riga corrente.
 */
export function usePeriodoData(gran, label, krFilter) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [azioni, setAzioni] = useState([]);
  const [mit, setMit] = useState([]);
  const [targets, setTargets] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchPeriodo(gran, label, krFilter);
      setData(d);
      setAzioni(d.azioni || []);
      setMit(d.mit || []);
      setTargets(d.target_periodo || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Errore caricamento';
      setError(msg);
      toast.error(`Errore caricamento periodo ${gran}/${label}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const reload = () => load();
    window.addEventListener('qp:quick-add-created', reload);
    return () => window.removeEventListener('qp:quick-add-created', reload);
    // `load` cattura gran/label/krFilter via closure: ricrearla ad ogni render
    // farebbe re-fetch in loop. Le deps sono le chiavi del periodo (corrette).
  }, [gran, label, krFilter]);

  // Helper: distingue occorrenza virtuale (id "{parent}::YYYY-MM-DD") da riga reale
  const isOccurrence = (item) => !!(item && item.is_occurrence && typeof item.id === 'string' && item.id.includes('::'));

  const toggleMitItem = (id, next, item) => {
    if (isOccurrence(item)) {
      // Optimistic update sull'array locale
      setMit((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: next } : x)));
      setRecurrenceOverride({
        parent_kind: 'mit',
        parent_id: item.parent_id,
        occurrence_date: item.occurrence_date,
        fatto: next,
      }).catch(() => {
        setMit((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: !next } : x)));
        toast.error('Salvataggio occorrenza fallito');
      });
      return;
    }
    // Riga reale: comportamento standard
    const rollback = () => setMit((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: !next } : x)));
    setMit((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: next } : x)));
    toggleMit(id, next).catch(() => { rollback(); toast.error('Salvataggio MIT fallito'); });
  };

  const toggleAzioneItem = (id, next, item) => {
    if (isOccurrence(item)) {
      setAzioni((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: next } : x)));
      setRecurrenceOverride({
        parent_kind: 'azione',
        parent_id: item.parent_id,
        occurrence_date: item.occurrence_date,
        fatto: next,
      }).catch(() => {
        setAzioni((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: !next } : x)));
        toast.error('Salvataggio occorrenza fallito');
      });
      return;
    }
    const rollback = () => setAzioni((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: !next } : x)));
    setAzioni((arr) => arr.map((x) => (x.id === id ? { ...x, fatto: next } : x)));
    toggleAzione(id, next).catch(() => { rollback(); toast.error('Salvataggio Azione fallito'); });
  };

  const toggleTappaItem = useOptimisticToggle(setTargets, (id, next) => toggleTargetPeriodo(id, next), 'raggiunto');

  return {
    data, loading, error,
    azioni, setAzioni,
    mit, setMit,
    targets,
    load,
    toggleMitItem, toggleAzioneItem, toggleTappaItem,
  };
}
